import { Column } from "@ant-design/plots";
import { useApiUrl, useCustom } from "@refinedev/core";
import { DatePicker, Descriptions, Drawer, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import {
  formatPeriodLabel,
  isOutsidePeriod,
  monthlySpendChartData,
  Period,
  periodDayBounds,
  periodFileToken,
  periodQueryParams,
} from "../lib/period";
import {
  InvoiceLineRow,
  OrderSummaryRow,
  Paginated,
  VesselConsumption,
  VesselOrderSummary,
} from "../lib/types";
import { ConsumptionPctCell } from "./ConsumptionPctCell";
import { ExportButtons } from "./ExportButtons";

const { RangePicker } = DatePicker;

// invoice-lines is the only flat-fee-analytics endpoint that takes a from/to
// window, so its RangePicker lives here rather than in the shared FilterBar.
// It narrows within the page period; it cannot reach outside it.
const INVOICE_LINES_PAGE_LIMIT = 25;

const money = (v: number) =>
  v.toLocaleString("en-US", { maximumFractionDigits: 2 });

// A narrowed window and a page number belong to the vessel and period they
// were chosen under. Both are stored with that identity and resolved during
// render, so a switch cannot leave one behind for the next vessel.
type Keyed<T> = { key: string; value: T };

interface VesselDetailDrawerProps {
  vessel: VesselConsumption | null;
  period: Period;
  onClose: () => void;
}

export const VesselDetailDrawer: React.FC<VesselDetailDrawerProps> = ({
  vessel,
  period,
  onClose,
}) => {
  const apiUrl = useApiUrl();
  const [narrowed, setNarrowed] = React.useState<Keyed<[Dayjs, Dayjs]> | null>(
    null
  );
  const [pageState, setPageState] = React.useState<Keyed<number> | null>(null);

  const windowKey = `${vessel?.vesselId ?? ""}|${periodFileToken(period)}`;
  const range =
    narrowed?.key === windowKey ? narrowed.value : periodDayBounds(period);
  const from = range[0].format("YYYY-MM-DD");
  const to = range[1].format("YYYY-MM-DD");

  // Page resets on a window change too, so a narrowed window can never ask
  // for a page past the end of its shorter list.
  const pageKey = `${windowKey}|${from}|${to}`;
  const invoicePage = pageState?.key === pageKey ? pageState.value : 1;

  const {
    result: invoiceLinesResult,
    query: { isLoading: invoiceLinesLoading },
  } = useCustom<Paginated<InvoiceLineRow>>({
    url: `${apiUrl}/flat-fee-analytics/vessels/${vessel?.vesselId}/invoice-lines`,
    method: "get",
    config: { query: { from, to, page: invoicePage, limit: INVOICE_LINES_PAGE_LIMIT } },
    queryOptions: { enabled: vessel !== null },
  });

  const invoiceLines = invoiceLinesResult?.data;

  const {
    result: orderSummaryResult,
    query: { isFetching: orderSummaryLoading },
  } = useCustom<VesselOrderSummary>({
    url: `${apiUrl}/flat-fee-analytics/vessels/${vessel?.vesselId}/orders`,
    method: "get",
    config: { query: periodQueryParams(period) },
    queryOptions: { enabled: vessel !== null },
  });

  // Same EMPTY_OBJECT trap as the list pages: useCustom's pre-load result is
  // a frozen truthy {}, so guard both fields before rendering.
  const orderSummary = orderSummaryResult?.data;
  const orderCount =
    typeof orderSummary?.orderCount === "number" ? orderSummary.orderCount : null;
  const topOrders = Array.isArray(orderSummary?.topOrders)
    ? orderSummary.topOrders
    : [];

  const chartData = monthlySpendChartData(vessel?.monthlySpend ?? []);

  const orderColumns: ColumnsType<OrderSummaryRow> = [
    { title: "Order", dataIndex: "orderNo", key: "orderNo", width: 140 },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 120,
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    { title: "Lines", dataIndex: "lineCount", key: "lineCount", width: 90 },
    {
      title: "Value",
      dataIndex: "spend",
      key: "spend",
      width: 120,
      render: (value) => money(value),
    },
  ];

  // Fixed widths + wrap-text is the project-wide table pattern
  // (src/styles/table.css): long values wrap inside their column so the
  // table never needs a horizontal scrollbar.
  const invoiceColumns: ColumnsType<InvoiceLineRow> = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      width: 140,
      className: "wrap-text",
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 120,
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    { title: "Order", dataIndex: "orderNo", key: "orderNo", width: 110 },
    {
      title: "Product",
      key: "product",
      width: 260,
      className: "wrap-text",
      render: (_, record) => record.productName ?? record.productId,
    },
    {
      title: "Price",
      dataIndex: "ukhoPublishedPrice",
      key: "ukhoPublishedPrice",
      width: 110,
      render: (value) => (value === null ? "-" : money(value)),
    },
    {
      title: "Currency",
      dataIndex: "invoiceCurrency",
      key: "invoiceCurrency",
      width: 100,
    },
  ];

  return (
    <Drawer
      open={vessel !== null}
      onClose={onClose}
      title={vessel?.vesselName}
      // Responsive with a readability cap: the invoice-lines table keeps
      // gaining columns, and a fixed 720px both crowded them and ran off
      // the left edge of a narrow screen. Past ~1200px a wide table reads
      // worse, not better - that is the point to move to a detail page.
      width="92%"
      styles={{ wrapper: { maxWidth: 1200 } }}
      destroyOnClose
      footer={
        vessel && (
          <div style={{ textAlign: "right" }}>
            <ExportButtons
              scope={{ scopeType: "vessel", scopeId: vessel.vesselId }}
              period={period}
            />
          </div>
        )
      }
    >
      {vessel && (
        <>
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
            <Descriptions.Item label="IMO">{vessel.imoNumber}</Descriptions.Item>
            <Descriptions.Item label="Shipco">
              {vessel.shipcoName ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Budget">
              {money(vessel.budget)} {vessel.budgetCurrency}
            </Descriptions.Item>
            <Descriptions.Item label="Spend">
              {money(vessel.spend)}
            </Descriptions.Item>
            <Descriptions.Item label="Consumption %">
              <ConsumptionPctCell
                pct={vessel.consumptionPct}
                budgetPending={vessel.budgetPending}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Orders">
              {orderCount ?? "-"}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Title level={5}>
            Top Orders by Value ({formatPeriodLabel(period)})
          </Typography.Title>
          <Table<OrderSummaryRow>
            rowKey="orderNo"
            size="small"
            loading={orderSummaryLoading}
            dataSource={topOrders}
            columns={orderColumns}
            pagination={false}
            style={{ marginBottom: 24 }}
          />

          <Typography.Title level={5}>
            Monthly Spend ({formatPeriodLabel(period)})
          </Typography.Title>
          <Column data={chartData} xField="month" yField="spend" height={240} />

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            Invoice Lines ({formatPeriodLabel(period)})
          </Typography.Title>
          <RangePicker
            allowClear
            style={{ marginBottom: 16 }}
            value={range}
            disabledDate={(date: Dayjs) => isOutsidePeriod(period, date)}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setNarrowed({ key: windowKey, value: [dates[0], dates[1]] });
              } else {
                // Clearing returns to the full page period, not to no filter.
                setNarrowed(null);
              }
            }}
          />
          <Table<InvoiceLineRow>
            rowKey={(record) =>
              `${record.invoiceNo}-${record.orderNo}-${record.productId}`
            }
            loading={invoiceLinesLoading}
            dataSource={invoiceLines?.data ?? []}
            columns={invoiceColumns}
            pagination={{
              current: invoiceLines?.page ?? invoicePage,
              pageSize: INVOICE_LINES_PAGE_LIMIT,
              total: invoiceLines?.total ?? 0,
              onChange: (p) => setPageState({ key: pageKey, value: p }),
            }}
          />
        </>
      )}
    </Drawer>
  );
};
